import { fetchAttachmentVariant } from '@lotushealth/lotushealth_js_sdk/endpoints';
import type {
  DocumentVariantRequestT,
  FetchAttachmentVariantRequestTVariant,
  FetchViewUrlForVariantReplyT,
  FileAttachmentTypeT,
  ImageVariantRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

export interface FetchAttachmentViewUrlRequest {
  userId: string;
  attachmentId: string;
  fileType: FileAttachmentTypeT;
}

function buildVariantRequest(fileType: FileAttachmentTypeT): FetchAttachmentVariantRequestTVariant {
  if (fileType === 'document') {
    // For documents, request the original variant.
    // The pages/bytesOfText params are ignored for 'original'.
    const variant: DocumentVariantRequestT = {
      variantType: 'original',
      pages: 0,
      bytesOfText: 0,
    };
    return variant;
  }

  // Default to image variant for images or unknown types.
  // The dimensions are ignored for 'original'.
  const variant: ImageVariantRequestT = {
    variantType: 'original',
    dimensions: { width: 0, height: 0 },
  };
  return variant;
}

/**
 * Mint a short-lived presigned URL for viewing/downloading an attachment.
 *
 * Uses the /care/chat attachment variant endpoint with the "original" variant
 * to fetch the full-resolution view URL.
 */
export async function fetchAttachmentViewUrl(
  request: FetchAttachmentViewUrlRequest,
  signal?: AbortSignal
): Promise<FetchViewUrlForVariantReplyT> {
  const config = await createLotusRequestConfig({ signal });
  const variant = buildVariantRequest(request.fileType);

  const response = await fetchAttachmentVariant(
    request.userId,
    request.attachmentId,
    { variant },
    config
  );
  return response.data;
}
